// Blogs.js — FULL UPDATE WITH AUTO-LINK NORMALIZATION + EMOJI SYSTEM + QUILL FIXES

import { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Api from "../../../../shared/api/api";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/AdminBlogs.css";
import "../styles/AdminSocialPosts.css";
import { DateTime } from "luxon";

function convertToUtc(localDatetime) {
  if (!localDatetime) return null;
  return DateTime.fromISO(localDatetime, { zone: "America/Los_Angeles" })
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

/* -------------------------------------------
   AUTO-LINK NORMALIZATION
------------------------------------------- */
function normalizeLink(url) {
  if (!url) return url;

  // Remove leading/trailing spaces
  url = url.trim();

  // Already absolute? Leave alone.
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // Otherwise prefix with https://
  return `https://${url}`;
}

/* Override Quill’s link creation */
const Link = Quill.import("formats/link");

class CustomLink extends Link {
  static sanitize(url) {
    return normalizeLink(url);
  }
}

Quill.register(CustomLink, true);

/* -------------------------------------------
   QUILL MODULES
------------------------------------------- */
const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
    handlers: {
      // Intercept Quill’s default link handler
      link: function () {
        const url = prompt("Enter URL:");
        if (url === null) return;

        const cleanUrl = normalizeLink(url);
        const editor = this.quill;
        const range = editor.getSelection(true);

        editor.format("link", cleanUrl);
      },
    },
  },
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

/* -------------------------------------------
   MAIN COMPONENT
------------------------------------------- */

export default function Blogs() {
  const defaultBlog = { title: "", content: "", image: "", tags: "" };
  const [blogs, setBlogs] = useState([]);
  const [blog, setBlog] = useState(defaultBlog);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalTitle, setOriginalTitle] = useState("");

  const quillRef = useRef(null);

  // Social share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareBlog, setShareBlog] = useState(null);
  const [fbBody, setFbBody] = useState("");
  const [fbFirstComment, setFbFirstComment] = useState("");
  const [igBody, setIgBody] = useState("");
  const [igFirstComment, setIgFirstComment] = useState("");
  const [twBody, setTwBody] = useState("");
  const [fbScheduledAt, setFbScheduledAt] = useState("");
  const [igScheduledAt, setIgScheduledAt] = useState("");
  const [twScheduledAt, setTwScheduledAt] = useState("");

  /* -------------------------------------------
     EMOJI SYSTEM
  ------------------------------------------- */
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("classic");

  const emojiCategories = {
    classic: ["😀","😃","😄","😁","😆","😅","😂","🤣","🙂","😉"],
    expressive: ["😊","😇","😍","🤩","😘","😎","🤔","😐","😶","🙄","😏","🥺"],
    dramatic: ["😤","😠","😡","😢","😭","😱","😨","😓","😥"],
    celebration: ["🔥","✨","💫","⚡","🎉","🎊","🏆","🌟","🌙","⭐"],
    writing: ["📚","✍️","📝","✒️","🎧","🕰️","🧭","🧩","🔍"],
    fantasy: ["🐉","🗡️","🛡️","🔮","🧙‍♂️","🧝‍♂️","🧛‍♂️","👁️"],
    utility: ["📸","🎬","🎵","💡","💬","📣","👀","🌄"],
  };

  const getFilteredEmojis = () => {
    let list = emojiCategories[activeCategory];
    if (!emojiSearch.trim()) return list;
    return list.filter((em) =>
      em.toLowerCase().includes(emojiSearch.toLowerCase())
    );
  };

  const insertEmojiAtCursor = (emoji) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    if (range) {
      editor.insertText(range.index, emoji);
      editor.setSelection(range.index + emoji.length);
      setBlog((prev) => ({ ...prev, content: editor.root.innerHTML }));
    }
  };

  /* -------------------------------------------
     FETCH BLOGS
  ------------------------------------------- */
  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const data = await Api.getAllAuthor("blog");
      const sorted = (data.blogs || data).sort((a, b) => {
        const parseDate = (str) => {
          if (!str) return 0;
          let clean = str.trim().replace(" ", "T");
          if (!clean.includes("T")) clean += "T00:00:00";
          const dt =
            DateTime.fromISO(clean, { zone: "utc" }).isValid
              ? DateTime.fromISO(clean, { zone: "utc" })
              : DateTime.fromFormat(clean, "yyyy-M-d HH:mm:ss.SSS", {
                  zone: "utc",
                });
          return dt.isValid ? dt.toMillis() : 0;
        };
        return parseDate(b.date) - parseDate(a.date);
      });

      setBlogs(sorted || []);
    } catch (err) {
      console.error("Error fetching blogs:", err);
    }
  };

  /* -------------------------------------------
     MODALS
  ------------------------------------------- */
  const openModal = () => {
    setShowModal(true);
    setIsEditMode(false);
    setOriginalTitle("");
    setBlog(defaultBlog);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setOriginalTitle("");
    setBlog(defaultBlog);
    setShowEmojiPicker(false);
  };

  /* -------------------------------------------
     FORM HANDLERS
  ------------------------------------------- */
  const handleBlogFieldChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setBlog({ ...blog, image: files[0] });
    } else {
      setBlog({ ...blog, [name]: value });
    }
  };

  const handleContentChange = (value) => {
    setBlog({ ...blog, content: value });
  };

  const handleSubmitBlog = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = blog.image;

      if (blog.image instanceof File) {
        const imgForm = new FormData();
        imgForm.append("imageFile", blog.image);
        imgForm.append("related_type", "blog");
        imgForm.append("related_id", blog.title);
        imgForm.append("alt_text", blog.title);
        imgForm.append("is_primary", "true");
        const res = await Api.uploadImage(imgForm);
        imageUrl = res.url;
      }

      const payload = {
        title: blog.title,
        content: blog.content || "",
        image: imageUrl || "",
        tags: blog.tags || "",
      };

      if (isEditMode) {
        const targetTitle = originalTitle || blog.title;
        await Api.updateBlog(targetTitle, payload);
      } else {
        await Api.createBlog(payload);
      }

      await fetchBlogs();
      closeModal();
    } catch (err) {
      console.error("Error submitting blog:", err);
    }
  };

  /* ------------------------------
     SHARE HANDLERS
  ------------------------------ */
  const handleShare = (b) => {
    const url = `https://kennethlpowell.com/blog/${encodeURIComponent(
      b.title
    )}`;
    setShareBlog(b);
    setFbBody("");
    setFbFirstComment(url);
    setIgBody("");
    setIgFirstComment(url);
    setTwBody(url);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareBlog(null);
  };

  const handleSubmitShare = async () => {
    if (!shareBlog) return;
    const img = shareBlog.image || null;
    try {
      if (fbBody.trim()) {
        await Api.createSocialPost({
          content: fbBody,
          image: img,
          platform: "facebook",
          first_comment: fbFirstComment,
          scheduled_at: convertToUtc(fbScheduledAt),
        });
      }
      if (igBody.trim()) {
        await Api.createSocialPost({
          content: igBody,
          image: img,
          platform: "instagram",
          first_comment: igFirstComment,
          scheduled_at: convertToUtc(igScheduledAt),
        });
      }
      if (twBody.trim()) {
        await Api.createSocialPost({
          content: twBody,
          image: null,
          platform: "twitter",
          scheduled_at: convertToUtc(twScheduledAt),
        });
      }
      alert("✔ Blog shared successfully!");
      closeShareModal();
    } catch (err) {
      console.error("Error sharing blog:", err);
      alert("❌ Failed to share blog.");
    }
  };

  /* -------------------------------------------
     EDIT / DELETE
  ------------------------------------------- */
  const handleEdit = (existingBlog) => {
    setBlog({
      ...defaultBlog,
      ...existingBlog,
      image: existingBlog.image,
    });
    setOriginalTitle(existingBlog.title);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (title) => {
    try {
      await Api.deleteBlog(title);
      await fetchBlogs();
    } catch (err) {
      console.error("Error deleting blog:", err);
    }
  };

  /* -------------------------------------------
     RENDER
  ------------------------------------------- */
  return (
    <div className="adminPageContainer">
      <h1>Manage Blogs</h1>
      <p><RouterLink to="/admin/settings/blog-videos">Blog & Author Videos settings →</RouterLink></p>
      <button className="addBookBtn" onClick={openModal}>
        Add Blog
      </button>

      {/* BLOG LIST */}
      <div className="adminBlogsList">
        {blogs.map((b) => (
          <div className="adminBlogRow" key={b.title}>
            <img
              src={
                b.image
                  ? b.image.includes("?")
                    ? `${b.image}&raw=1`
                    : `${b.image}?raw=1`
                  : "https://via.placeholder.com/150x100?text=No+Image"
              }
              alt={b.title}
              className="adminBlogThumb"
            />

            <div className="adminBlogInfo">
              <h3>{b.title}</h3>
              <p>{b.date}</p>
              <p className="blogContentSnippet">
                {b.content
                  ? b.content.replace(/<[^>]+>/g, "").slice(0, 120) + "..."
                  : ""}
              </p>
            </div>

            <div className="adminBlogActions">
              <button
                className="adminBlogEditBtn"
                onClick={() => handleEdit(b)}
              >
                Edit
              </button>
              <button
                className="adminBlogDeleteBtn"
                onClick={() => handleDelete(b.title)}
              >
                Delete
              </button>
              <button
                className="adminMerchShareBtn"
                onClick={() => handleShare(b)}
              >
                Share
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* BLOG MODAL */}
      {showModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h2>{isEditMode ? "Edit Blog" : "Add Blog"}</h2>

            <form className="blogFormGrouped" onSubmit={handleSubmitBlog}>
              <div className="formSection">

                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={blog.title}
                  onChange={handleBlogFieldChange}
                  required
                />

                <label>Content</label>
                <div className="richTextEditorWrapper">
                  <button
                    type="button"
                    className="emojiToggleBtn"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                  >
                    😀 Emoji
                  </button>

                  {showEmojiPicker && (
                    <div className="emojiPickerContainer">
                      <input
                        type="text"
                        className="emojiSearchInput"
                        placeholder="Search emoji..."
                        value={emojiSearch}
                        onChange={(e) => setEmojiSearch(e.target.value)}
                      />

                      <div className="emojiCategoryTabs">
                        {Object.keys(emojiCategories).map((cat) => (
                          <div
                            key={cat}
                            className="emojiCategoryTab"
                            style={{
                              background:
                                activeCategory === cat
                                  ? "#e3d9f8"
                                  : "#f1ecfa",
                            }}
                            onClick={() => setActiveCategory(cat)}
                          >
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </div>
                        ))}
                      </div>

                      <div className="emojiGrid">
                        {getFilteredEmojis().map((em, i) => (
                          <button
                            key={`${activeCategory}_${i}`}
                            type="button"
                            className="emojiBtn"
                            onClick={() => insertEmojiAtCursor(em)}
                          >
                            {em}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <ReactQuill
                    ref={quillRef}
                    value={blog.content || ""}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ height: "600px", marginBottom: "1rem" }}
                  />
                </div>

                <label>Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  value={blog.tags}
                  onChange={handleBlogFieldChange}
                />
              </div>

              <div className="formSection">
                <label>Image</label>
                <input
                  type="file"
                  name="image"
                  onChange={handleBlogFieldChange}
                />
                {typeof blog.image === "string" && blog.image && (
                  <img
                    src={
                      blog.image.includes("?")
                        ? `${blog.image}&raw=1`
                        : `${blog.image}?raw=1`
                    }
                    alt={blog.title}
                    className="adminBlogThumb"
                  />
                )}
              </div>

              <div className="buttonRow">
                <button className="publishingSubmitBtn" type="submit">
                  {isEditMode ? "Update Blog" : "Submit Blog"}
                </button>
                <button
                  className="cancelBtn"
                  type="button"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SHARE BLOG MODAL */}
      {showShareModal && (
        <div className="modalOverlay" onClick={closeShareModal}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h2>Share Blog</h2>

            <div className="platformsContainer">
              <div className="formSection">
                <h3>Facebook</h3>
                <textarea
                  value={fbBody}
                  onChange={(e) => setFbBody(e.target.value)}
                />
                <input
                  type="datetime-local"
                  value={fbScheduledAt}
                  onChange={(e) => setFbScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="First comment or link"
                  value={fbFirstComment}
                  onChange={(e) => setFbFirstComment(e.target.value)}
                />
              </div>

              <div className="formSection">
                <h3>Instagram</h3>
                <textarea
                  value={igBody}
                  onChange={(e) => setIgBody(e.target.value)}
                />
                <input
                  type="datetime-local"
                  value={igScheduledAt}
                  onChange={(e) => setIgScheduledAt(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="First comment or link"
                  value={igFirstComment}
                  onChange={(e) => setIgFirstComment(e.target.value)}
                />
              </div>

              <div className="formSection">
                <h3>Twitter/X</h3>
                <textarea
                  value={twBody}
                  onChange={(e) => setTwBody(e.target.value)}
                  maxLength={280}
                />
                <p>{twBody.length} / 280</p>
                <input
                  type="datetime-local"
                  value={twScheduledAt}
                  onChange={(e) => setTwScheduledAt(e.target.value)}
                />
              </div>
            </div>

            <div className="buttonRow">
              <button className="publishingSubmitBtn" onClick={handleSubmitShare}>
                Post
              </button>
              <button className="cancelBtn" onClick={closeShareModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
